<template>
  <div class="raw-charges-tab">
    <div class="raw-charges-tab-data-table w-6">
      <h2 class="section-subheader">{{ t('rateShop.charges.charges', 2) }}</h2>

      <DataTable
        :value="rateQuote.rawCharges"
        class="rate-detail-table p-datatable-sm"
        auto-layout
      >
        <Column :header="t('rateShop.charges.code')" field="code" class="w-1">
          <template #body="{ data }">
            {{ data.code }}

            <div
              v-if="lowerCase(data.code) !== lowerCase(data.description)"
              class="text-gray-darker sub-heading"
            >
              {{ data.description }}
            </div>
          </template>
        </Column>

        <Column
          :header="t('rateShop.charges.stop')"
          field="stopSequenceNumber"
          class="w-1"
        >
          <template #body="{ data }">
            <StopSequenceNumberTooltip :data="data" />
          </template>
        </Column>

        <Column :header="t('rateShop.charges.amount')" class="w-1 text-right">
          <template #body="{ data: rateQuoteCharge }">
            <div class="text-right">
              {{
                renderMoney(rateQuoteCharge.charge, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>
          <template #footer>
            <div class="text-right text-success font-medium">
              {{
                renderMoney(rateQuote.totalPrice, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>
        </Column>
      </DataTable>

      <div class="p-data-table-uplift-details">
        <h2 class="section-subheader">
          {{ t('rateShop.details.upliftDetails') }}
        </h2>
        <DataTable
          :value="collectRateQuoteUpliftDetails(rateQuote)"
          class="rate-detail-table p-datatable-sm datatable-no-header"
          auto-layout
        >
          <Column field="k" header-style="display:none;" />
          <Column field="v" header-style="display:none;" />
        </DataTable>
      </div>
    </div>

    <div class="quote-details w-6">
      <RateQuoteDetails :rate-quote="rateQuote" :email-tender="emailTender" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { lowerCase, renderMoney } from '@rygen/rygen-ui/i18n';

import { RateQuote } from '@/model';

import RateQuoteDetails from '@/components/rateShop/expandedDetails/RateQuoteDetails.vue';
import StopSequenceNumberTooltip from '@/components/rateShop/expandedDetails/StopSequenceNumberTooltip.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  emailTender: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, emailTender } = toRefs(props);

const { t } = useI18n();

const collectRateQuoteUpliftDetails = (
  rateQuote: RateQuote,
): { k: string; v: string }[] => {
  const details: { k: string; v: string }[] = [];
  details.push({
    k: t('rateShop.details.upliftAmount'),
    v: renderMoney(rateQuote.upliftAmount, {
      currencyCode: rateQuote.currencyCode,
    }),
  });
  if (rateQuote.upliftRule?.upliftRate) {
    details.push({
      k: t('rateShop.details.upliftRate'),
      v: `${rateQuote.upliftRule.upliftRate} ${rateQuote.upliftRule.upliftRateUnit}`,
    });
  }
  details.push({
    k: t('rateShop.details.upliftMinMax'),
    v:
      renderMoney(rateQuote.upliftRule?.minimumAmount, {
        currencyCode: rateQuote.currencyCode,
      }) +
      ' / ' +
      renderMoney(rateQuote.upliftRule?.maximumAmount, {
        currencyCode: rateQuote.currencyCode,
        defaultValue: '∞',
      }),
  });
  return details;
};
</script>

<style>
.raw-charges-tab {
  display: flex;
  gap: var(--sp-3);

  .quote-details {
    border-left: 1px solid var(--divider-color);
    padding-inline: var(--sp-3);
  }
}
</style>
